import {Inject, Injectable, PLATFORM_ID, signal} from '@angular/core';
import {isPlatformBrowser} from '@angular/common';
import {HttpClient} from '@angular/common/http';
import {Router} from '@angular/router';
import {environment} from '@/environments/environment';
import {map, Observable, throwError} from 'rxjs';
import {User} from '@/app/core/models/user';
import {catchError, tap} from 'rxjs/operators';
import {UserService} from '@services/user/user.service';

@Injectable({
	providedIn: 'root'
})
export class AuthService {

	private readonly SERVICE_URI = `${environment.backendUrl}/auth/twitch`;
	private readonly _user = signal<User | null>(null);
	private readonly isBrowser: boolean;

	constructor(
		private readonly http: HttpClient,
		private readonly router: Router,
		private readonly userService: UserService,
		@Inject(PLATFORM_ID) platformId: object,
	) {
		this.isBrowser = isPlatformBrowser(platformId);
	}

	public storeToken(accessToken: string): void {
		if (!this.isBrowser) return;
		localStorage.setItem('access_token', accessToken);
	}

	public getToken(): string | null {
		// Pas de session persistée côté serveur (SSR/prerender) : jamais authentifié.
		if (!this.isBrowser) return null;
		return <string>localStorage.getItem('access_token');
	}

	public isAuthenticated(): boolean {
		const token = this.getToken();
		if (!token) {
			return false;
		} else {
			return true;
		}
	}

	/**
	 * Logs out the user by calling the logout endpoint and clearing the session storage.
	 * Redirects to the home page after logout.
	 */
	logout(): void {
		const token = this.getToken();
		if (!token) return;

		this.http.post(`${this.SERVICE_URI}/logout`, {}, {
			headers: { Authorization: `Bearer ${token}` }
		}).subscribe({
			next: () => {
				this.destroyToken();
				this._user.set(null);
				this.router.navigate(['']);
			},
			error: (err) => {
				console.error('Logout failed', err);
				this.destroyToken();
				this.router.navigate(['']);
			}
		});
	}

	private destroyToken(): void {
		if (!this.isBrowser) return;
		localStorage.removeItem('access_token');
	}

	/**
	 * Refreshes the access token by calling the refresh endpoint.
	 * @returns An Observable that emits the new access token on success.
	 */
	refreshToken(): Observable<string> {
		const token = this.getToken();

		if (!token) {
			this.logout();
			return throwError(() => new Error('No access token found'));
		}

		return this.http.post<{ access_token: string }>(
			`${this.SERVICE_URI}/refresh`,
			{},
			{
				headers: token ? { Authorization: `Bearer ${token}` } : {}
			}
		).pipe(
			map(response => {
				this.storeToken(response.access_token);
				return response.access_token;
			}),
			catchError(err => {
				console.error('Refresh token failed', err);
				this.logout();
				return throwError(err);
			})
		);
	}

	loadUser(): Observable<User> {
		return this.userService.getUserProfile().pipe(
			tap(user => this._user.set(user))
		);
	}

	getUser(): User | null {
		return this._user();
	}

	getCoins(): number {
		return this._user()?.coins ?? 0;
	}

	get userInitial(): string {
		const firstChar = this._user()?.username?.at(0);
		return firstChar ? firstChar.toUpperCase() : '';
	}

	isBetaTester(): boolean {
		return this._user()?.roles.includes('BETA_TESTER') ?? false;
	}

	deleteAccount(): void {
		this.destroyToken();
		this._user.set(null);
	}
}
